import { QuestionType, Question } from '../types'
import {
  QuestionTypeConfig,
  QuestionRendererProps,
} from '../../practice/components/renderers/types'
import { getValidator } from './validatorRegistry'
import React from 'react'

// Renderers
import { MultipleChoiceRenderer } from '../../practice/components/renderers/MultipleChoiceRenderer'
import { ColumnArithmeticRenderer } from '../../practice/components/renderers/ColumnArithmeticRenderer'
import { LongDivisionRenderer } from '../../practice/components/renderers/LongDivisionRenderer'
import { MoneyAssemblerRenderer } from '../../practice/components/renderers/MoneyAssemblerRenderer'
import { ClockRenderer } from '../../practice/components/renderers/ClockRenderer'
import { FillInRenderer } from '../../practice/components/renderers/FillInRenderer'
import { OrderingRenderer } from '../../practice/components/renderers/OrderingRenderer'
import { MatchingRenderer } from '../../practice/components/renderers/MatchingRenderer'
import { DragDropFillRenderer } from '../../practice/components/renderers/DragDropFillRenderer'
import { TableReadingRenderer } from '../../practice/components/renderers/TableReadingRenderer'
import { NumberSequenceRenderer } from '../../practice/components/renderers/NumberSequenceRenderer'
import { RatioTableRenderer } from '../../practice/components/renderers/RatioTableRenderer'
import { TextHighlightRenderer } from '../../practice/components/renderers/TextHighlightRenderer'
import { BarChartRenderer } from '../../practice/components/renderers/BarChartRenderer'
import { LineChartRenderer } from '../../practice/components/renderers/LineChartRenderer'
import { ScheduleRenderer } from '../../practice/components/renderers/ScheduleRenderer'
import { PieChartRenderer } from '../../practice/components/renderers/PieChartRenderer'
import { MultiplicationGridRenderer } from '../../practice/components/renderers/MultiplicationGridRenderer'
import { TrueFalseSeriesRenderer } from '../../practice/components/renderers/TrueFalseSeriesRenderer'
import { EstimationSliderRenderer } from '../../practice/components/renderers/EstimationSliderRenderer'
import { CategorizeRenderer } from '../../practice/components/renderers/CategorizeRenderer'
import { ErrorSpottingRenderer } from '../../practice/components/renderers/ErrorSpottingRenderer'
import { GridShadeRenderer } from '../../practice/components/renderers/GridShadeRenderer'
import { StepSolverRenderer } from '../../practice/components/renderers/StepSolverRenderer'
import { SentenceParserRenderer } from '../../practice/components/renderers/SentenceParserRenderer'
import { ComparisonJudgeRenderer } from '../../practice/components/renderers/ComparisonJudgeRenderer'
import { VerbConjugationFlowRenderer } from '../../practice/components/renderers/VerbConjugationFlowRenderer'
import { ThermometerSliderRenderer } from '../../practice/components/renderers/ThermometerSliderRenderer'
import { SentenceErrorJudgeRenderer } from '../../practice/components/renderers/SentenceErrorJudgeRenderer'

/**
 * Question Type Registry v3.0
 *
 * Part of the Content domain (DDD Fase 3).
 * Moved from src/config/questionTypeRegistry.ts in R25.
 *
 * Modern architecture with clean separation of concerns:
 * - questionTypeRegistry.ts → renderer mapping + metadata
 * - validatorRegistry.ts → all validation logic
 * - answerProtocol.ts → answer payload types + bridge functions
 */

/** Metadata-only config for a question type (no validation logic) */
interface QuestionTypeMeta {
  type: QuestionType
  label: string
  isInteractive: boolean
  hasBuiltInVisuals: boolean
  shouldShuffleOptions: boolean
  isCitoEligible: boolean
  validateAnswer: (userAnswer: string | number, question: Question) => boolean
  Renderer: React.ComponentType<QuestionRendererProps>
}
const QUESTION_TYPE_META: Record<QuestionType, QuestionTypeMeta> = {
  multiple_choice: {
    type: 'multiple_choice',
    label: 'Meerkeuze',
    isInteractive: false,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: true,
    isCitoEligible: true,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('multiple_choice', userAnswer, question),
    Renderer: React.memo(MultipleChoiceRenderer),
  },
  cijferend_rekenen: {
    type: 'cijferend_rekenen',
    label: 'Cijferend Rekenen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('cijferend_rekenen', userAnswer, question),
    Renderer: React.memo(ColumnArithmeticRenderer),
  },
  cijferend_delen: {
    type: 'cijferend_delen',
    label: 'Cijferend Delen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('cijferend_delen', userAnswer, question),
    Renderer: React.memo(LongDivisionRenderer),
  },
  clock: {
    type: 'clock',
    label: 'Klokkijken',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('clock', userAnswer, question),
    Renderer: React.memo(ClockRenderer),
  },
  money: {
    type: 'money',
    label: 'Geld',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('money', userAnswer, question),
    Renderer: React.memo(MoneyAssemblerRenderer),
  },
  fill_in: {
    type: 'fill_in',
    label: 'Invulvraag',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('fill_in', userAnswer, question),
    Renderer: React.memo(FillInRenderer),
  },
  ordering: {
    type: 'ordering',
    label: 'Volgorde',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('ordering', userAnswer, question),
    Renderer: React.memo(OrderingRenderer),
  },
  matching: {
    type: 'matching',
    label: 'Koppelen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('matching', userAnswer, question),
    Renderer: React.memo(MatchingRenderer),
  },
  drag_drop_fill: {
    type: 'drag_drop_fill',
    label: 'Sleep en Plaats',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: true,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('drag_drop_fill', userAnswer, question),
    Renderer: React.memo(DragDropFillRenderer),
  },
  table_reading: {
    type: 'table_reading',
    label: 'Tabel Aflezen',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('table_reading', userAnswer, question),
    Renderer: React.memo(TableReadingRenderer),
  },
  number_sequence: {
    type: 'number_sequence',
    label: 'Getallenreeks',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('number_sequence', userAnswer, question),
    Renderer: React.memo(NumberSequenceRenderer),
  },
  ratio_table: {
    type: 'ratio_table',
    label: 'Verhoudingstabel',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('ratio_table', userAnswer, question),
    Renderer: React.memo(RatioTableRenderer),
  },
  text_highlight: {
    type: 'text_highlight',
    label: 'Tekst Markeren',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('text_highlight', userAnswer, question),
    Renderer: React.memo(TextHighlightRenderer),
  },
  bar_chart: {
    type: 'bar_chart',
    label: 'Staafgrafiek',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('bar_chart', userAnswer, question),
    Renderer: React.memo(BarChartRenderer),
  },
  line_chart: {
    type: 'line_chart',
    label: 'Lijngrafiek',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('line_chart', userAnswer, question),
    Renderer: React.memo(LineChartRenderer),
  },
  schedule_reading: {
    type: 'schedule_reading',
    label: 'Rooster Lezen',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('schedule_reading', userAnswer, question),
    Renderer: React.memo(ScheduleRenderer),
  },
  pie_chart: {
    type: 'pie_chart',
    label: 'Cirkeldiagram',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('pie_chart', userAnswer, question),
    Renderer: React.memo(PieChartRenderer),
  },
  multiplication_grid: {
    type: 'multiplication_grid',
    label: 'Tafelrooster',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('multiplication_grid', userAnswer, question),
    Renderer: React.memo(MultiplicationGridRenderer),
  },
  true_false_series: {
    type: 'true_false_series',
    label: 'Waar/Onwaar Reeks',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('true_false_series', userAnswer, question),
    Renderer: React.memo(TrueFalseSeriesRenderer),
  },
  estimation_slider: {
    type: 'estimation_slider',
    label: 'Schatting',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('estimation_slider', userAnswer, question),
    Renderer: React.memo(EstimationSliderRenderer),
  },
  categorize: {
    type: 'categorize',
    label: 'Sorteren',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: true,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('categorize', userAnswer, question),
    Renderer: React.memo(CategorizeRenderer),
  },
  error_spotting: {
    type: 'error_spotting',
    label: 'Fouten Zoeken',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('error_spotting', userAnswer, question),
    Renderer: React.memo(ErrorSpottingRenderer),
  },

  grid_shade: {
    type: 'grid_shade',
    label: 'Raster Arceren',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('grid_shade', userAnswer, question),
    Renderer: React.memo(GridShadeRenderer),
  },

  step_solver: {
    type: 'step_solver',
    label: 'Stapsgewijs Oplossen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('step_solver', userAnswer, question),
    Renderer: React.memo(StepSolverRenderer),
  },

  sentence_parser: {
    type: 'sentence_parser',
    label: 'Zinsontleding',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('sentence_parser', userAnswer, question),
    Renderer: React.memo(SentenceParserRenderer),
  },

  comparison_judge: {
    type: 'comparison_judge',
    label: 'Vergelijken',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('comparison_judge', userAnswer, question),
    Renderer: React.memo(ComparisonJudgeRenderer),
  },

  verb_conjugation_flow: {
    type: 'verb_conjugation_flow',
    label: 'Werkwoord Vervoegen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('verb_conjugation_flow', userAnswer, question),
    Renderer: React.memo(VerbConjugationFlowRenderer),
  },

  thermometer_slider: {
    type: 'thermometer_slider',
    label: 'Thermometer Slider',
    isInteractive: true,
    hasBuiltInVisuals: true,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('thermometer_slider', userAnswer, question),
    Renderer: React.memo(ThermometerSliderRenderer),
  },

  sentence_error_judge: {
    type: 'sentence_error_judge',
    label: 'Zinsfout Herkennen',
    isInteractive: true,
    hasBuiltInVisuals: false,
    shouldShuffleOptions: false,
    isCitoEligible: false,
    validateAnswer: (userAnswer, question) =>
      validateAnswer('sentence_error_judge', userAnswer, question),
    Renderer: React.memo(SentenceErrorJudgeRenderer),
  },
}

/**
 * Build the QUESTION_TYPE_REGISTRY by combining
 * metadata from QUESTION_TYPE_META with validators from ValidatorRegistry.
 */
export const QUESTION_TYPE_REGISTRY: Record<QuestionType, QuestionTypeConfig> =
  Object.fromEntries(
    Object.entries(QUESTION_TYPE_META).map(([key, meta]) => [
      key,
      {
        ...meta,
        validateAnswer: (userAnswer: string | number, question: Question) =>
          getValidator(key)(userAnswer, question),
      },
    ]),
  ) as Record<QuestionType, QuestionTypeConfig>
export function getQuestionTypeConfig(type: string): QuestionTypeConfig {
  if (type === 'geld_samenstellen' || type === 'klok_aflezen') {
    throw new Error(
      `Obsolete questionType "${type}" detected. Migrate to canonical "money" or "clock".`,
    )
  }
  const config = QUESTION_TYPE_REGISTRY[type as QuestionType]
  if (!config) {
    throw new Error(
      `Unsupported questionType "${type}" in questionTypeRegistry.`,
    )
  }
  return config
}
